"use client";

import Link from "@/lib/router/link";
import { usePathname } from "@/lib/router/navigation";
import {
  Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent,
  SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarRail,
} from "@/components/ui/sidebar";
import { Logo } from "@/components/brand/logo";
import { DataSourceBadge } from "@/components/shell/data-source-badge";
import { accountNavItems, navItemShown } from "@/config/nav";
import { useTenancy } from "@/lib/tenancy/tenancy-context";
import { useAiTestingShown } from "@/lib/hooks/use-ai-testing-available";
import { cn } from "@/lib/utils";

export function AccountSidebar() {
  const pathname = usePathname();
  const { can } = useTenancy();
  // 2.854: the AI testing entries follow the served menu, fail-open.
  const aiShown = useAiTestingShown();

  const isActive = (href: string, exact?: boolean) =>
    exact ? pathname === href : pathname === href || pathname.startsWith(href + "/");

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader className="gap-2.5 px-3 pb-1 pt-4">
        <Link href="/overview" className="flex h-9 items-center group-data-[collapsible=icon]:justify-center">
          <Logo showName className="group-data-[collapsible=icon]:hidden" />
          <Logo showName={false} className="hidden group-data-[collapsible=icon]:flex" />
        </Link>
      </SidebarHeader>

      <SidebarContent className="px-2.5">
        <SidebarGroup className="py-1">
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5">
              {accountNavItems.filter((it) => navItemShown(it, can, aiShown)).map((item) => {
                const active = isActive(item.href, item.exact);
                return (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton asChild isActive={active} tooltip={item.title}
                      className={cn("h-9 gap-2.5 rounded-md text-[13px] font-medium transition-colors",
                        active ? "bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary" : "text-foreground/70 hover:bg-accent hover:text-foreground")}>
                      <Link href={item.href} aria-current={active ? "page" : undefined}>
                        <item.icon className={cn("size-[18px]", active && "text-primary")} />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="px-3 pb-3 group-data-[collapsible=icon]:hidden">
        <DataSourceBadge />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
